import { Box, Button, Chip, Divider, IconButton, Link, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { colors } from "../../../ui/colors";
import { JobScopeOfWorkView } from "../../jobs-list/jobs-layout/components/Summary/JobScopeOfWorkView";
import { normalizeCostOverride } from "../../jobs-list/jobs-layout/components/Summary/BasicJob";
import { useJobsByIdQuery } from "../../jobs-list/jobs-layout/hooks/useJobByIdQuery";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { JobBasicEdit } from "../components/JobBasicEdit";
import { JobScopeOfWorkEdit } from "../components/JobScopeOfWorkEdit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { EBenefitTypes } from "../../../../permissions/benefits/enums/benefits.enums";
import { AccessGate, DisabledBehavior, useBenefitStrategy } from "../../../../permissions/access";
import { formatJobServiceCostCurrency, resolveJobServiceCost } from "../utils/resolveJobServiceCost";

const CONTEXT_BLOCK_HEIGHT = 320;

interface Props {
  jobId: string | number;
  onViewFullScope?: () => void;
}

type TaskChip = {
  key: string;
  title: string;
  subtitle?: string;
};

type AreaGroup = {
  areaKey: string;
  areaName: string;
  areaDescription?: string;
  quantity: number;
  tasks: TaskChip[];
};

type BasicJobSummary = {
  description?: string;
  frequency: string;
  unitPrice?: number;
  units?: number;
  unitLabel?: string;
  total?: number;
  currency: string;
  showMultiplication: boolean;
};

const MEASURE_LABELS: Record<string, string> = {
  sqft: "sq ft",
  sqm: "sqm",
  minutes: "minutes",
  hours: "hours",
  days: "days",
  weeks: "weeks",
  months: "months",
  per_item: "items",
  unit: "units",
  unique: "one-time",
};

const sowUi = {
  pageBg: "#F8F9FA",
  cardBg: "#FFFFFF",
  border: "var(--fx-surface-border, #E2E8F0)",
  shadow: "0px 4px 20px rgba(0,0,0,0.03)",
  title: colors.gray[800],
  text: colors.gray[600],
  muted: colors.gray[400],
};

const sowLabelSx = {
  fontSize: "0.66rem",
  fontWeight: 800,
  color: sowUi.muted,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  lineHeight: 1.15,
};

const sowValueSx = {
  fontSize: "0.82rem",
  fontWeight: 600,
  color: sowUi.title,
  lineHeight: 1.35,
};

const sowCardSx = {
  borderRadius: 3,
  bgcolor: sowUi.cardBg,
  border: `1px solid ${sowUi.border}`,
  boxShadow: sowUi.shadow,
};

function formatFrequencyLabel(value?: string | null): string {
  if (!value) return "—";

  return value
    .replace(/^by_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatMeasureLabel(by?: string | null): string {
  if (!by) return "units";

  return MEASURE_LABELS[by.toLowerCase()] ?? by;
}

const handleOpenScopePDF = (job: any) => {
  const url = job?.scopeOfWorkUrl;
  if (url) {
    const cacheBustedUrl = url.includes("?") ? `${url}&t=${Date.now()}` : `${url}?t=${Date.now()}`;
    window.open(cacheBustedUrl, "_blank", "noopener,noreferrer");
  }
};

export function JobScopeOfWork({ jobId, onViewFullScope }: Props) {
  const access = useBenefitStrategy(EBenefitTypes.SOW_PDF);

  const { data: job, isLoading } = useJobsByIdQuery({ idStr: jobId });

  const [expanded, setExpanded] = useState(false);

  const { serviceNameOverride, originalServiceName, areaGroups, totalTasks, basicJobSummary } = useMemo(() => {
    const scope = job?.scopeOfWork;

    const service = scope?.typeOfService;

    const serviceNameOverride = scope?.serviceNameOverride?.trim() || null;

    const originalServiceName = service?.name?.trim() || scope?.name?.trim() || "Service";

    const areas = scope?.areasByScopesOfWork ?? [];

    const isBasicJob = job?.type === "basic";

    const groups: AreaGroup[] = isBasicJob
      ? []
      : areas
          .filter((area: any) => Number(area?.quantity ?? 0) > 0)
          .map((area: any) => {
            const rawTasks = area?.tasks ?? [];

            const taskList: TaskChip[] = Array.isArray(rawTasks)
              ? rawTasks
                  .map((task: any, index: number) => ({
                    key: String(task?.uid ?? task?.id ?? `${area?.uid ?? "area"}-${index}`),
                    title: String(task?.name ?? task?.title ?? task ?? "Task"),
                    subtitle: typeof task === "object" && task?.description ? stripSeedTags(String(task.description)) : undefined,
                  }))
                  .filter((task) => task.title.trim().length > 0)
              : [];

            return {
              areaKey: String(area?.uid ?? area?.id ?? area?.area?.name ?? area?.name ?? Math.random()),
              areaName: area?.area?.name ?? area?.name ?? "Area",
              areaDescription: area?.area?.description ? stripSeedTags(String(area.area.description)) : undefined,
              quantity: Number(area?.quantity ?? 0),
              tasks: taskList,
            };
          });

    const count = groups.reduce((accumulator, group) => accumulator + group.tasks.length, 0);

    let basicSummary: BasicJobSummary | null = null;

    if (isBasicJob && service && job) {
      const serviceCost = resolveJobServiceCost(job as any, scope as any);

      const costOverride = normalizeCostOverride(scope?.costOverride) as Record<string, unknown> | null;

      const measureBy = String(costOverride?.by ?? costOverride?.measure ?? serviceCost?.unitLabel ?? "");

      basicSummary = {
        description: service.description?.trim() || undefined,
        frequency: formatFrequencyLabel(scope?.frequencyType),
        unitPrice: serviceCost?.unitPrice,
        units: serviceCost?.units,
        unitLabel: serviceCost?.unitLabel ?? formatMeasureLabel(measureBy),
        total: serviceCost?.amount,
        currency: serviceCost?.currency ?? String(costOverride?.currency ?? "USD"),
        showMultiplication: Boolean(serviceCost?.showMultiplication),
      };
    }

    return {
      serviceNameOverride,
      originalServiceName,
      areaGroups: groups,
      totalTasks: count,
      basicJobSummary: basicSummary,
    };
  }, [job]);

  const isBasicJob = job?.type === "basic";
  const hasBasicScopeData = isBasicJob && Boolean(job?.scopeOfWork?.typeOfService);
  const hasAdvancedScopeData = !isBasicJob && Boolean(job?.scopeOfWork?.typeOfService);
  const hasAreas = areaGroups.length > 0;
  const maxVisibleGroups = 4;
  const visibleGroups = expanded ? areaGroups : areaGroups.slice(0, maxVisibleGroups);
  const hiddenGroupsCount = Math.max(areaGroups.length - maxVisibleGroups, 0);

  const emptyMessage = !job?.scopeOfWork?.typeOfService ? "No scope of work configured for this job." : "No areas configured for this scope.";

  if (isLoading) {
    return (
      <Box
        sx={{
          width: { xs: "100%", md: "45%" },
          p: 2,
          height: CONTEXT_BLOCK_HEIGHT,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 550 }}>Scope of work</Typography>
        <Box
          sx={{
            flex: 1,
            mt: 1.5,
            borderRadius: 2,
            border: `1px dashed ${colors.gray[300]}`,
            backgroundColor: colors.gray[50],
          }}
        />
      </Box>
    );
  }

  if (!job) return null;

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "45%" },
        p: 2,
        height: CONTEXT_BLOCK_HEIGHT,
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        borderRadius: 4,
        bgcolor: sowUi.pageBg,
        boxShadow: sowUi.shadow,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              ...sowLabelSx,
              mb: 0.35,
            }}
          >
            Scope of work
          </Typography>

          <Typography
            sx={{
              fontSize: "0.95rem",
              fontWeight: 750,
              color: sowUi.title,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={serviceNameOverride || originalServiceName}
          >
            {hasBasicScopeData || hasAdvancedScopeData ? serviceNameOverride || originalServiceName : "—"}
          </Typography>
          {serviceNameOverride && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: sowUi.muted,
                fontWeight: 500,
                mt: 0.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={originalServiceName}
            >
              Original service: {originalServiceName}
            </Typography>
          )}
          {hasAdvancedScopeData ? (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: sowUi.muted,
                fontWeight: 500,
                mt: 0.35,
              }}
            >
              {totalTasks} tasks
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            flexShrink: 0,
          }}
        >
          {isBasicJob ? (
            <BaseModalComponent
              modalKey={`Job-Edit-${job.id}`}
              modalLabel="Edit Job"
              defaultSizes={{ width: 750, height: 600 }}
              elementTrigger={
                <Tooltip title="Edit job">
                  <IconButton
                    size="small"
                    aria-label="Edit job"
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      color: colors.gray[500],
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      border: `1px solid ${sowUi.border}`,
                      "&:hover": {
                        color: colors.orange.base,
                        bgcolor: colors.gray[50],
                      },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              }
            >
              <Box sx={{ height: "85vh", display: "flex", flexDirection: "column" }}>
                <JobBasicEdit job={job} scopeOfWork={job.scopeOfWork} />
              </Box>
            </BaseModalComponent>
          ) : null}

          {hasAdvancedScopeData && job.scopeOfWork ? (
            <BaseModalComponent
              modalKey={`Job-Edit-ScopeOfWork-${job.id}`}
              modalLabel="Edit scope of work"
              defaultSizes={{ width: 1200 }}
              elementTrigger={
                <Tooltip title="Edit scope of work">
                  <IconButton
                    size="small"
                    aria-label="Edit scope of work"
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 2,
                      color: colors.gray[500],
                      bgcolor: "var(--fx-surface-base, #FFFFFF)",
                      border: `1px solid ${sowUi.border}`,
                      "&:hover": {
                        color: colors.orange.base,
                        bgcolor: colors.gray[50],
                      },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              }
            >
              <JobScopeOfWorkEdit job={job as any} scopeOfWork={job.scopeOfWork as any} />
            </BaseModalComponent>
          ) : null}

          {hasAdvancedScopeData ? (
            <AccessGate
              strategy={access}
              behavior={DisabledBehavior}
              behaviorConfig={{
                featureName: "SOW PDF",
                tooltip: "Upgrade your plan to unlock this feature",
              }}
            >
              <Button
                onClick={() => handleOpenScopePDF(job)}
                sx={{
                  minWidth: "auto",
                  height: 30,
                  px: 1,
                  gap: 0.6,
                  textTransform: "none",
                  borderRadius: 2,
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  border: `1px solid ${colors.orange.base}`,
                  color: colors.orange.base,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: colors.orange.base,
                    color: "#fff",
                  },
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 15 }} />

                <Typography
                  sx={{
                    fontSize: "0.76rem",
                    fontWeight: 700,
                    color: "inherit",
                  }}
                >
                  PDF
                </Typography>
              </Button>
            </AccessGate>
          ) : null}
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5, borderColor: sowUi.border }} />

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
        {hasBasicScopeData && basicJobSummary ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            <Box
              sx={{
                ...sowCardSx,
                p: 1.35,
              }}
            >
              <Chip
                label="Basic service"
                size="small"
                sx={{
                  height: 23,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  bgcolor: colors.orange[100],
                  color: colors.orange.base,
                  mb: basicJobSummary.description ? 1 : 0,
                }}
              />

              {basicJobSummary.description ? (
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    color: sowUi.text,
                    fontWeight: 500,
                    lineHeight: 1.45,
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {basicJobSummary.description}
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1,
              }}
            >
              <Box sx={{ ...sowCardSx, p: 1.2 }}>
                <Typography sx={sowLabelSx}>Frequency</Typography>
                <Typography sx={{ ...sowValueSx, mt: 0.35 }}>{basicJobSummary.frequency}</Typography>
              </Box>

              {basicJobSummary.total != null && basicJobSummary.total > 0 ? (
                <Box sx={{ ...sowCardSx, p: 1.2 }}>
                  <Typography sx={sowLabelSx}>Total</Typography>
                  <Typography
                    sx={{
                      ...sowValueSx,
                      mt: 0.35,
                      color: colors.orange.base,
                      fontWeight: 800,
                    }}
                  >
                    {formatJobServiceCostCurrency(basicJobSummary.total, basicJobSummary.currency)}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            {basicJobSummary.showMultiplication && basicJobSummary.unitPrice != null && basicJobSummary.units != null ? (
              <Box sx={{ ...sowCardSx, p: 1.25 }}>
                <Typography sx={sowLabelSx}>Pricing</Typography>

                <Typography
                  sx={{
                    ...sowValueSx,
                    mt: 0.45,
                  }}
                >
                  {formatJobServiceCostCurrency(basicJobSummary.unitPrice, basicJobSummary.currency)} × {basicJobSummary.units}{" "}
                  {basicJobSummary.unitLabel}
                </Typography>
              </Box>
            ) : basicJobSummary.total != null && basicJobSummary.total > 0 ? (
              <Box sx={{ ...sowCardSx, p: 1.25 }}>
                <Typography sx={sowLabelSx}>Pricing</Typography>

                <Typography
                  sx={{
                    ...sowValueSx,
                    mt: 0.45,
                  }}
                >
                  {formatJobServiceCostCurrency(basicJobSummary.total, basicJobSummary.currency)}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ) : !hasAreas ? (
          <Box
            sx={{
              height: "100%",
              borderRadius: 3,
              border: `1px dashed ${colors.gray[300]}`,
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: sowUi.text,
                fontWeight: 500,
              }}
            >
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {visibleGroups.map((g) => (
              <Box
                key={g.areaKey}
                sx={{
                  ...sowCardSx,
                  p: 1.25,
                  minWidth: 0,
                }}
              >
                {/* Area header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1.5,
                    mb: 0.9,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        fontWeight: 700,
                        color: sowUi.title,
                        lineHeight: 1.3,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={g.areaDescription || g.areaName}
                    >
                      {g.areaName} × {g.quantity}
                    </Typography>
                  </Box>

                  <Chip
                    label={`${g.tasks.length} tasks`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      bgcolor: colors.gray[50],
                      color: colors.gray[500],
                      flexShrink: 0,
                    }}
                  />
                </Box>

                {/* Tasks chips */}
                {g.tasks.length === 0 ? (
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: sowUi.muted,
                      fontWeight: 500,
                    }}
                  >
                    No tasks for this area.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.65 }}>
                    {g.tasks.slice(0, expanded ? g.tasks.length : 8).map((t) => (
                      <Chip
                        key={t.key}
                        label={t.title}
                        size="small"
                        title={t.subtitle || t.title}
                        sx={{
                          height: 24,
                          borderRadius: "999px",
                          fontSize: "0.74rem",
                          fontWeight: 500,
                          bgcolor: "var(--fx-surface-base, #FFFFFF)",
                          border: `1px solid ${sowUi.border}`,
                          color: colors.gray[600],
                          "& .MuiChip-label": {
                            px: 1,
                          },
                        }}
                      />
                    ))}

                    {!expanded && g.tasks.length > 8 && (
                      <Chip
                        label={`+${g.tasks.length - 8} more`}
                        size="small"
                        onClick={() => setExpanded(true)}
                        sx={{
                          height: 24,
                          borderRadius: "999px",
                          fontSize: "0.74rem",
                          fontWeight: 600,
                          bgcolor: colors.gray[50],
                          border: `1px dashed ${colors.gray[300]}`,
                          color: colors.gray[500],
                          cursor: "pointer",
                        }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            ))}

            {!expanded && hiddenGroupsCount > 0 && (
              <Box sx={{ mt: 0.25 }}>
                <Chip
                  label={`Show ${hiddenGroupsCount} more areas`}
                  size="small"
                  onClick={() => setExpanded(true)}
                  sx={{
                    height: 28,
                    borderRadius: "999px",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                    border: `1px dashed ${colors.gray[300]}`,
                    color: colors.gray[600],
                    cursor: "pointer",
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Footer actions */}
      {hasAdvancedScopeData && hasAreas ? (
        <Box
          sx={{
            mt: 1.25,
            pt: 1,
            borderTop: `1px solid ${sowUi.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Link
            component="button"
            onClick={() => setExpanded((v) => !v)}
            sx={{
              fontSize: "0.78rem",
              color: colors.gray[500],
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": {
                color: colors.orange.base,
                textDecoration: "underline",
              },
            }}
          >
            {expanded ? "Show less" : "Show all"}
          </Link>

          <BaseModalComponent
            modalKey="Scope-of-work-view-full"
            modalLabel={`Scope of work - Job: #${job.id}`}
            elementTrigger={
              <Box>
                <Link
                  component="button"
                  sx={{
                    fontSize: "0.78rem",
                    color: colors.orange.base,
                    fontWeight: 700,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View full scope
                </Link>
              </Box>
            }
          >
            <JobScopeOfWorkView scope={job.scopeOfWork} type={job.type} jobDescription={job.description} />
          </BaseModalComponent>
        </Box>
      ) : hasBasicScopeData ? (
        <Box
          sx={{
            mt: 1.25,
            pt: 1,
            borderTop: `1px solid ${sowUi.border}`,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <BaseModalComponent
            modalKey="Scope-of-work-view-full"
            modalLabel={`Scope of work - Job: #${job.id}`}
            elementTrigger={
              <Box>
                <Link
                  component="button"
                  sx={{
                    fontSize: "0.78rem",
                    color: colors.orange.base,
                    fontWeight: 700,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  View service details
                </Link>
              </Box>
            }
          >
            <JobScopeOfWorkView scope={job.scopeOfWork} type={job.type} jobDescription={job.description} />
          </BaseModalComponent>
        </Box>
      ) : null}
    </Box>
  );
}

function stripSeedTags(text: string) {
  return text.replace(/\s*@\w+/g, "").trim();
}
